import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { DxNumberBoxComponent } from 'devextreme-angular/ui/number-box';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { convertFriendlyExpirationToIso, DetectMethodChanges, determineDateFormat, isVoid, makeCollarsOptionDisplayName } from '../../utils';
import { LoggerService } from '../../logging/logger-factory.service';
import { Logger } from '../../logging/logger.interface';
import { ShellClientService } from '../../shell-communication/shell-client.service';
import { MessageBusService } from '../../message-bus.service';
import { LastQuoteCacheService } from '../../last-quote-cache.service';
import { OptionsChainService } from '../../option-chains.service';
import { TradingInstrument } from '../../trading-instruments/trading-instrument.class';
import { TradingInstrumentKind } from '../../trading-instruments/trading-instrument-kind.enum';
import { TradingInstrumentsService } from '../../trading-instruments/trading-instruments-service.interface';
import { TradingInstrumentDisplayNameService } from '../../trading-instruments/trading-instrument-display-name.service';
import { makeOptionTicker, parseOptionTicker } from '../../options-common/options.model';
import { GetOptionChainShellResponse, OptionExpirationDescriptor } from '../../shell-communication/shell-dto-protocol';
import { QuoteDto } from '../../shell-communication/dtos/quote-dto.class';
import { CopyOrdersToService } from '../../adjustment-pricing-grid/services/copy-orders-to.service';
import {
   CollarsBucketDto,
   CollarsPositionDto,
   ListCollarsBuckets,
   ListCollarsPositions,
   RecordCollarsTransaction
} from '../../shell-communication/collars-operations';
import { CollarsAfterStateService } from './collars-after-state.service';
import { CollarsTransactionDiffService } from './collars-transaction-diff.service';
import { CollarsComboSettingsService } from '../collars-combo-settings.service';
import { CollarsAnalysisLockService } from '../collars-analysis-lock.service';
import { CollarsCopyToTosAdapter } from './collars-copy-to-tos.adapter';
import {
   CollarsDiffResult,
   CollarsEditableLeg,
   CollarsMakeTransactionConfig,
   CollarsMakeTransactionResult,
   CollarsTransactionDelta
} from './collars-after-state.models';
import { CollarsAcquireFill, CollarsAfterStatePreviewComponent } from './collars-after-state-preview.component';
import { CollarsAfterStateRowsContext, toAfterStateRows } from './collars-after-state-rows';

// Position kinds (match CollarsPositionDto.kind / RecordCollarsTransaction.kind).
const KIND_ASSET = 0;
const KIND_OPTION = 1;

// Bucket levels (match CollarsBucketDto.level).
const LEVEL_PORTFOLIO = 0;
const LEVEL_COMBO = 1;
const LEVEL_GROUP = 2;

// Dialog-local extension of the shared CollarsEditableLeg. Two extra fields support the existing-leg roll +
// the per-leg instrument resolution:
//   - underlyingSymbol: the underlying parsed from a leg's OWN code (existing legs may sit on a different
//     underlying than the symbol picker; a roll composes against THIS one).
//   - expirationDescriptor: the OptionExpirationDescriptor resolved from the leg's own underlying chain. It
//     seeds the inline expiration/strike pickers + the roll; distinct from `expiration` (the raw date string).
//
// CLONE-AND-REMOVE model (HG parity, simplified per owner): changing an existing option leg's strike or
// expiration crosses the ORIGINAL leg and adds a NORMAL new editable leg (isNew = true, NO clonedFromId /
// supersedes binding). A rolled leg carries a FIXED, pre-composed tradingInstrumentCode + a real descriptor,
// so codeOf returns it directly rather than re-composing through the symbol picker.
interface MtEditableLeg extends CollarsEditableLeg {
   underlyingSymbol?: string;
   expirationDescriptor?: OptionExpirationDescriptor;
   // HG qty-split state (clone of HedgePad selectedForTrans/transQty): focusing an EXISTING leg's qty
   // splits the cell — the original qty becomes read-only and `transQty` takes the DESIRED REMAINING
   // magnitude (not the delta). A valid transQty is committed to targetQty/qty (the end-state the diff
   // reads); an invalid one reverts the split on focus-out.
   qtySplit?: boolean;
   transQty?: number;
}

/**
 * Make-Transaction dialog. The leg editor is an EXACT clone of the Hedging Grid HedgePad single-hedge leg
 * block (colored Buy/Sell select, centered qty number box, ets-expiration-selector, Call/Put select,
 * ets-strikes-dropdown, fa remove/undo icons, 'Add Option'/'Add Asset' totals row). HG components are NOT
 * imported — the markup + styling are re-derived (Collars never imports HG/APG).
 *
 * The operator edits a leaf group's desired END-STATE in-line; the derived diff(current positions, edited
 * end-state) -> Before / Transaction / After is shown in a SEPARATE, PRICE-FREE pop-up
 * (ets-collars-after-state-preview), opened by the Preview button.
 *
 *  - SAVE persists the END-STATE legs per leaf group to user settings (one key per touched leaf group) for
 *    later Apply After State; nothing is booked.
 *  - DIRECT COMMIT derives the transaction, then collects prices in its OWN step (NOT in the price-free
 *    preview) and books each derived leg via RecordCollarsTransaction.
 *
 * Promise-based: show() resolves a result describing whether the operator saved and/or committed.
 *
 * Edit primitives shape the END-STATE only; the apply/preview diff re-derives Close/Open/Increase/Decrease.
 * They mirror the Hedging Grid leg editor: qty (negative & zero BLOCKED), X = cross-out (existing restorable,
 * new removed), side-flip / strike / expiry change = clone-and-remove.
 */
@Component({
   selector: 'ets-collars-make-transaction-dialog',
   templateUrl: './collars-make-transaction-dialog.component.html',
   styleUrls: ['./collars-make-transaction-dialog.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollarsMakeTransactionDialogComponent implements OnInit, OnDestroy {

   constructor(
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _toastr: ToastrService,
      private readonly _shellClient: ShellClientService,
      private readonly _messageBus: MessageBusService,
      private readonly _lastQuoteCache: LastQuoteCacheService,
      private readonly _optionChains: OptionsChainService,
      // CopyOrdersToService is @Injectable() (NOT providedIn root) and is provided per-feature-module (APG,
      // Package Comparison, EtsCollarsModule). Injected @Optional() so the dialog still constructs/DI-verifies
      // when the provider is absent; copyOrdersToTos degrades to a toast.
      @Optional() private readonly _copyOrders: CopyOrdersToService,
      private readonly _afterState: CollarsAfterStateService,
      private readonly _diff: CollarsTransactionDiffService,
      private readonly _comboSettings: CollarsComboSettingsService,
      private readonly _analysisLocks: CollarsAnalysisLockService,
      private readonly _tradingInstruments: TradingInstrumentsService,
      private readonly _displayNames: TradingInstrumentDisplayNameService,
      loggerService: LoggerService
   ) {
      this._logger = loggerService.createLogger('CollarsMakeTransactionDialog');
   }

   private readonly _logger: Logger;

   // The separate, price-free Before/Transaction/After preview pop-up (clone of hg-after-state-preview).
   @ViewChild(CollarsAfterStatePreviewComponent)
   previewDialog: CollarsAfterStatePreviewComponent;

   // Buy/Sell display values bridged to the leg's numeric side (1 = Buy, -1 = Sell). The leg editor edits the
   // STRING (HG idiom: colored Buy/Sell select); side-flip maps it back to a sign.
   readonly sideTexts = ['Buy', 'Sell'];
   readonly optionTypeTexts = ['Call', 'Put'];

   // Centered number-box text — exactly the HG dxCenterTextStyleObj (applied via [inputAttr]).
   readonly dxCenterTextStyleObj = { style: 'text-align: center' };

   isVisible = false;
   busy = false;

   private _config: CollarsMakeTransactionConfig;
   private _resolve: (result: CollarsMakeTransactionResult) => void;
   private _seq = 0;

   // The loaded bucket tree (for the new-leg target-Group picker). Loaded with positions on show().
   private _buckets: CollarsBucketDto[] = [];

   // The scope's current OPEN positions (the "Before" face value), loaded on show().
   private _currentPositions: CollarsPositionDto[] = [];

   // The editable end-state (HedgePad parity). Seeded from the scope's current positions (one row each).
   editableLegs: MtEditableLeg[] = [];

   // The shared underlying for NEW legs: the symbol picker sets it; its option chain drives the expiration
   // list; every new leg's instrument is resolved against it.
   underlying: TradingInstrument = null;
   expirationsList: OptionExpirationDescriptor[] = [];

   // Option chains for EVERY distinct underlying present in the loaded positions, keyed by underlying symbol.
   // Existing option legs may sit on different underlyings than the symbol picker; an inline strike/expiry
   // roll of an existing leg must resolve + compose against ITS OWN underlying's chain, not the picker's.
   private _chainsByUnderlying: { [underlyingSymbol: string]: GetOptionChainShellResponse } = {};

   // (The former Direct-Commit price step is gone — owner workflow 2026-06-10: acquisition prices are
   // collected IN the review pop-up's acquisition mode and booked via bookAcquisition.)

   // Live reference-quote subscriptions are torn down on hide/destroy. Quotes arrive on the QuoteDto bus.
   private readonly _unsub = new Subject<void>();

   // Subscribe the live reference-quote bus ONCE (this dialog is a reused @ViewChild instance).
   ngOnInit(): void {
      this._messageBus.of<QuoteDto[]>('QuoteDto')
         .pipe(takeUntil(this._unsub))
         .subscribe((m: { payload: QuoteDto[] }) => this.onQuotes(m.payload));
   }

   ngOnDestroy(): void {
      this.unsubscribeAllLegQuotes();
      this._unsub.next();
      this._unsub.complete();
   }

   @DetectMethodChanges()
   show(config: CollarsMakeTransactionConfig): Promise<CollarsMakeTransactionResult> {
      this._config = config;
      this.editableLegs = [];
      this._currentPositions = [];
      this._buckets = [];
      this.underlying = null;
      this.expirationsList = [];
      this._chainsByUnderlying = {};
      this.busy = false;
      this.isVisible = true;
      // The QuoteDto bus is subscribed once in ngOnInit; live ticks update each leg's quote while visible.
      this.loadScope();
      return new Promise<CollarsMakeTransactionResult>(resolve => { this._resolve = resolve; });
   }

   @DetectMethodChanges()
   onHidden(): void {
      this.unsubscribeAllLegQuotes();
      this.isVisible = false;
      this.resolveWith({ saved: false, committed: false });
   }

   @DetectMethodChanges()
   onCancel(): void {
      this.unsubscribeAllLegQuotes();
      this.isVisible = false;
      this.resolveWith({ saved: false, committed: false });
   }

   // ---- Load + seed ----

   private async loadScope(): Promise<void> {
      this.busy = true;
      this._changeDetector.detectChanges();
      try {
         const buckets = await this._shellClient.processQuery<CollarsBucketDto[]>(new ListCollarsBuckets());
         const all = await this._shellClient.processQuery<CollarsPositionDto[]>(new ListCollarsPositions());
         this._buckets = buckets || [];
         const scoped = this.scopeCurrentPositions(all || []);
         this._currentPositions = scoped;
         this.editableLegs = scoped.map(p => this.toEditableLeg(p));
         // Load the option chain for every distinct underlying present among the existing option legs, then
         // resolve each existing option leg's current expiration to its OptionExpirationDescriptor from ITS
         // OWN underlying's chain. This seeds the existing-leg inline expiry/strike pickers + the roll.
         await this.loadChainsForExistingLegs();
         // When the scope is single-underlying (all existing option legs share one underlying), DEFAULT the
         // Symbol picker to it via the SAME path a manual pick uses (onSymbolSelected loads expirationsList +
         // re-syncs picker legs). Multi-underlying scope leaves the picker empty (existing legs still resolve
         // their own per-underlying chains above). This awaits its own getChain, so re-sync existing-leg
         // quotes afterwards.
         await this.defaultUnderlyingFromScope();
         this.editableLegs.forEach(l => this.syncLegQuote(l));
      } catch (e) {
         this._logger.error('Collars make-transaction: loading scope (buckets/positions) failed', { error: e.stack || e });
         this._toastr.error('Could Not Load Current Positions');
      } finally {
         this.busy = false;
         this._changeDetector.detectChanges();
      }
   }

   // Load the option chain for every DISTINCT underlying present among the seeded existing OPTION legs, key
   // each chain by the CANONICAL chain key, then resolve each existing option leg's current expiration DATE
   // STRING to its OptionExpirationDescriptor from ITS underlying's chain.
   //
   // The chain key is NOT the raw token parsed from the leg's code — it is the CANONICAL instrument ticker
   // resolved via getInstrumentByTicker, exactly the blotter path (collars.component.ts loadPositionIntoBlotter
   // -> getInstrumentByTicker(parsed.underlying) -> getChain(instrument.ticker)). Subscribing the chain by the
   // raw token can miss the registry's canonical key, leaving the chain unloaded -> the descriptor unresolved
   // -> no quote. We resolve each leg's chain key ONCE here and store it on the leg so the load + the lookup +
   // the strikes-dropdown all key the same chain.
   private async loadChainsForExistingLegs(): Promise<void> {
      const keys: string[] = [];
      this.editableLegs.forEach(l => {
         if (l.isNew || l.kind !== KIND_OPTION) { return; }
         const key = this.chainKeyFor(l.underlyingSymbol);
         l.underlyingSymbol = key;   // canonicalize so every downstream lookup keys the SAME chain
         if (!isVoid(key) && keys.indexOf(key) < 0) { keys.push(key); }
      });
      for (let i = 0; i < keys.length; i++) {
         const key = keys[i];
         try {
            const chain = await this._optionChains.getChain(key);
            if (chain) { this._chainsByUnderlying[key] = chain; }
         } catch (e) {
            this._logger.error('Collars make-transaction: load chain for existing-leg underlying failed', { underlying: key, error: e.stack || e });
         }
      }
      this.editableLegs.forEach(l => {
         if (l.isNew || l.kind !== KIND_OPTION) { return; }
         l.expirationDescriptor = this.resolveDescriptor(l.underlyingSymbol, l.expiration);
      });
   }

   // The CANONICAL chain key for a raw underlying token parsed from a leg's option code. Route the token
   // through the instrument registry (getInstrumentByTicker) and prefer the registered ticker — the exact key
   // getChain / the chain descriptors are keyed by (GetOptionChainShellQH sets UnderlyingTicker =
   // instrument.Ticker). Fall back to the raw token when the registry has no entry (better than dropping it).
   private chainKeyFor(rawUnderlying: string): string {
      if (isVoid(rawUnderlying)) { return rawUnderlying; }
      const inst = this._tradingInstruments.getInstrumentByTicker(rawUnderlying);
      return (inst && !isVoid(inst.ticker)) ? inst.ticker : rawUnderlying;
   }

   // Normalize an expiration DATE STRING to the chain's canonical ISO form (yyyy-MM-dd). A leg's code date
   // token may be the friendly form (dd-MMM-yy) when the position originated from TOS copy / retro-fill /
   // transfer, while the chain descriptor's optionExpirationDate is ALWAYS yyyy-MM-dd (GetOptionChainShellQH).
   // An exact === would miss the friendly form -> null descriptor -> no quote, so normalize before matching.
   private toIsoExpiration(expirationDate: any): string {
      if (isVoid(expirationDate)) { return null; }
      const s = String(expirationDate);
      return determineDateFormat(s) === 'friendly' ? convertFriendlyExpirationToIso(s) : s;
   }

   // Resolve an underlying + expiration DATE STRING to the matching OptionExpirationDescriptor from that
   // underlying's loaded chain (match by optionExpirationDate, normalized to ISO on both sides). Returns null
   // when unavailable.
   private resolveDescriptor(underlyingSymbol: string, expirationDate: any): OptionExpirationDescriptor {
      if (isVoid(underlyingSymbol) || isVoid(expirationDate)) { return null; }
      const chain = this._chainsByUnderlying[underlyingSymbol];
      if (!chain || !chain.expirations) { return null; }
      const wanted = this.toIsoExpiration(expirationDate);
      const match = chain.expirations.find(d => this.toIsoExpiration(d.optionExpirationDate) === wanted);
      return match || null;
   }

   // Default the Symbol picker (this.underlying) when the scope is unambiguously SINGLE-UNDERLYING
   // (owner rule 2026-06-10: infer "absolutely, 100%" — never guess between candidates):
   //   1) ASSET legs name the underlying directly — their stored code IS the underlying ticker.
   //   2) The Underlyings bucket can be EMPTY while option positions exist: deconstruct the OPTION legs —
   //      the underlying is baked into every option ticker. Prefer the already-canonicalized chain key
   //      (loadChainsForExistingLegs); fall back to a raw parse of the stored code for legs whose earlier
   //      parse never resolved.
   // Exactly ONE distinct underlying -> route it through onSymbolSelected (the SAME path a manual pick
   // uses) so expirationsList, legExpirations, the strikes-dropdown enable + existingLegInstrument() all
   // light up. Ambiguous / unresolved scopes leave the picker empty (existing legs still resolve their own
   // per-underlying chains) — each bail point logs WHY so a silent no-default stays diagnosable.
   private async defaultUnderlyingFromScope(): Promise<void> {
      if (!isVoid(this.underlying)) { return; }

      const keys: string[] = [];
      const push = (key: string) => {
         if (!isVoid(key) && keys.indexOf(key) < 0) { keys.push(key); }
      };

      // 1) Asset legs first — the direct statement of the underlying.
      this.editableLegs.forEach(l => {
         if (l.isNew || l.kind !== KIND_ASSET) { return; }
         push(this.chainKeyFor(l.tradingInstrumentCode));
      });

      // 2) No asset in scope: deconstruct the option positions.
      if (keys.length === 0) {
         this.editableLegs.forEach(l => {
            if (l.isNew || l.kind !== KIND_OPTION) { return; }
            let key = l.underlyingSymbol;
            if (isVoid(key)) {
               const parsed = parseOptionTicker(l.tradingInstrumentCode);
               key = parsed ? this.chainKeyFor(parsed.underlying) : undefined;
            }
            push(key);
         });
      }

      if (keys.length === 0) {
         this._logger.debug('Collars make-transaction: no underlying inferable from the scope (no asset legs, no parseable option legs)');
         return;
      }
      if (keys.length > 1) {
         this._logger.debug(`Collars make-transaction: multi-underlying scope (${keys.join(', ')}) — Symbol picker left empty`);
         return;
      }
      const inst = this._tradingInstruments.getInstrumentByTicker(keys[0]);
      if (isVoid(inst)) {
         this._logger.debug(`Collars make-transaction: inferred underlying '${keys[0]}' has no registered instrument — Symbol picker left empty`);
         return;
      }
      await this.onSymbolSelected(inst);
   }

   // The expiration descriptors an EXISTING option leg's inline expiry picker offers — its OWN underlying's
   // chain. Empty until that chain is loaded.
   existingLegExpirations(leg: MtEditableLeg): OptionExpirationDescriptor[] {
      const chain = this._chainsByUnderlying[leg.underlyingSymbol];
      return (chain && chain.expirations) ? chain.expirations : [];
   }

   // The expiration descriptors a NEW option leg's picker offers. A rolled leg (it already carries its own
   // descriptor/underlying) offers its OWN underlying's chain; a genuinely-new picker leg offers the symbol
   // picker's chain.
   legExpirations(leg: MtEditableLeg): OptionExpirationDescriptor[] {
      if (!isVoid(leg.underlyingSymbol)) {
         const chain = this._chainsByUnderlying[leg.underlyingSymbol];
         if (chain && chain.expirations) { return chain.expirations; }
      }
      return this.expirationsList;
   }

   // The strikes-dropdown target instrument for an EXISTING option leg. ets-strikes-dropdown reads its strike
   // LIST from the expiration descriptor's `strikes[]` (which IS the leg's own chain), but requires a non-null
   // targetInstrument to enable. The chain response carries no TradingInstrument, so we hand it the symbol
   // picker's instrument — exactly the original wiring (`[targetInstrument]="underlying"`). The roll itself
   // composes the REAL ticker via makeOptionTicker against the leg's OWN descriptor, so booking is correct
   // regardless of which instrument the dropdown is pointed at.
   existingLegInstrument(leg: MtEditableLeg): TradingInstrument {
      return this.underlying;
   }

   // A NEW leg can pick an expiration/strike only once its symbol is chosen. A rolled leg carries its own
   // underlying; a genuinely-new picker leg depends on the symbol picker.
   legHasSymbol(leg: MtEditableLeg): boolean {
      if (!isVoid(leg.underlyingSymbol)) { return true; }
      return !isVoid(this.underlying);
   }

   // Keep only the OPEN positions inside the opened scope: net != 0, not Interest, not archived.
   private scopeCurrentPositions(all: CollarsPositionDto[]): CollarsPositionDto[] {
      const id = this._config.scopeBucketId;
      const level = this._config.level;
      return all.filter(p => {
         if (p.netPosition === 0) { return false; }
         if (p.isInterest) { return false; }
         if (p.isArchived) { return false; }
         if (level === LEVEL_PORTFOLIO) { return p.portfolioId === id; }
         if (level === LEVEL_COMBO) { return p.comboId === id; }
         return p.groupId === id;
      });
   }

   // Map one current position to an editable end-state row.
   private toEditableLeg(p: CollarsPositionDto): MtEditableLeg {
      const mag = Math.abs(p.netPosition);
      const side = p.netPosition >= 0 ? 1 : -1;
      const parsed = p.kind === KIND_OPTION ? this.parseOption(p.tradingInstrumentCode) : null;
      return {
         id: ++this._seq,
         isNew: false,
         crossedOut: false,
         restorable: true,
         portfolioId: p.portfolioId || undefined,
         comboId: p.comboId || undefined,
         groupBucketId: p.groupId,
         kind: p.kind,
         tradingInstrumentCode: p.tradingInstrumentCode,
         side,
         qty: mag,
         originalQty: mag,
         targetQty: mag,
         optionType: parsed ? parsed.optionType : undefined,
         strike: parsed ? parsed.strike : undefined,
         expiration: parsed ? parsed.expiration : undefined,
         underlyingSymbol: parsed ? parsed.underlying : undefined,
         expirationDescriptor: undefined,   // resolved in loadChainsForExistingLegs once the chain is loaded
         liveQuote: this.resolveLiveQuote(p.tradingInstrumentCode, p.kind)
      };
   }

   // Seed a leg's reference quote from the cache, under the SAME convention pickQuote enforces on live
   // ticks: option legs show MID, asset legs show LAST — no cross-fallback. Type-safe checks on purpose:
   // isVoid(0) is true for numbers, and a legitimate 0 mid (0-bid/0-ask option) must NOT fall back to LAST.
   private resolveLiveQuote(ticker: string, kind: number): number | undefined {
      const q = this._lastQuoteCache.getLastQuote(ticker);
      if (isVoid(q)) { return undefined; }
      if (kind === KIND_OPTION) {
         return (typeof q.mid === 'number' && !isNaN(q.mid)) ? q.mid : undefined;
      }
      return (typeof q.lastPx === 'number' && !isNaN(q.lastPx)) ? q.lastPx : undefined;
   }

   // Best-effort split of an EXISTING composed option ticker. parseOptionTicker is the same parser the panel
   // uses ("<expTicker> <ul> <Call|Put> <strike> <date>").
   private parseOption(code: string): { optionType?: string; strike?: number; expiration?: any; underlying?: string } | null {
      if (isVoid(code)) { return null; }
      const t = parseOptionTicker(code);
      if (!t) { return null; }
      return { optionType: t.type, strike: t.strike, expiration: t.expiration, underlying: t.underlying };
   }

   // ---- Edit primitives (they shape the END-STATE only) ----

   // The operator types the desired end-state magnitude. Negative & ZERO are BLOCKED (HG parity).
   @DetectMethodChanges()
   onTargetQtyChange(leg: MtEditableLeg, value: number): void {
      if (isVoid(value) || value <= 0) {
         this._toastr.error('Quantity Must Be a Positive Number');
         return;
      }
      leg.targetQty = value;
      leg.qty = value;
   }

   // X = cross-out. An existing leg is excluded but restorable (back-arrow undoes it); a brand-new leg is
   // removed outright (and its quote subscription dropped).
   @DetectMethodChanges()
   onCrossOut(leg: MtEditableLeg): void {
      if (leg.isNew) {
         this.dropLegQuote(leg);
         this.editableLegs = this.editableLegs.filter(x => x !== leg);
      } else {
         // Settle any open qty-split FIRST: a crossed leg prices/books the FULL close, so a lingering split
         // would contradict it visually, stay keyboard-reachable, and make a later Restore silently diff as
         // a Decrease (targetQty stuck at the stale desired qty). HG resets trans state on its removed
         // clone for the same reason (cloneAndRemoveLeg -> resetTransState).
         this.settleQtySplit(leg);
         leg.crossedOut = true;
      }
   }

   // Close a leg's qty-split and return it to face value (the end-state qty the diff reads reverts to the
   // original magnitude). Callers that CLONE the committed desired qty must build the clone BEFORE settling.
   private settleQtySplit(leg: MtEditableLeg): void {
      if (!leg.qtySplit) { return; }
      leg.qtySplit = false;
      leg.transQty = undefined;
      leg.targetQty = leg.originalQty;
      leg.qty = leg.originalQty;
   }

   // Restore a crossed-out existing leg.
   @DetectMethodChanges()
   onRestore(leg: MtEditableLeg): void {
      leg.crossedOut = false;
   }

   // Duplicate a leg (Multi-Trade Pad parity, far fa-clone): push a NEW editable copy — same instrument,
   // side, qty, group. The copy is a normal new leg (renders in the new-legs section, fully editable);
   // the operator tweaks it from there — committing an untouched duplicate stays blocked by the
   // one-line-per-(group, instrument) validation.
   @DetectMethodChanges()
   onDuplicateLeg(leg: MtEditableLeg): void {
      // OPTIONS only: the package holds at most ONE asset leg (the asset rows render no icon; this guard
      // is the backstop).
      if (leg.crossedOut || leg.kind === KIND_ASSET) { return; }
      if (!this.ensureSingleUnderlyingForAdd()) { return; }
      const copy = this.makeRolledLeg(leg, {});
      this.insertAfter(leg, copy);
      this.syncLegQuote(copy);
   }

   // A NEW leg that carries a BAKED instrument code (a duplicate of an existing leg, or a roll) must not
   // keep that code once the operator edits strike/expiration/type — codeOf would book the OLD instrument.
   // Recompose against the leg's own expiration source; if it cannot compose yet, CLEAR the baked code so
   // codeOf/validation treat the leg as picker-driven instead of silently using the stale instrument.
   private refreshNewLegCode(leg: MtEditableLeg): void {
      if (!leg.isNew || leg.kind !== KIND_OPTION) { return; }
      if (isVoid(leg.tradingInstrumentCode) || !String(leg.tradingInstrumentCode).trim()) { return; }
      const exp: any = leg.expirationDescriptor || leg.expiration;
      const code = (!isVoid(exp) && !isVoid(leg.optionType) && leg.strike != null && leg.strike > 0)
         ? makeOptionTicker(exp, leg.optionType, leg.strike)
         : null;
      leg.tradingInstrumentCode = code || '';
   }

   // ---- Qty focus-split (HG HedgePad clone: onQtyFocusIn / transQty / onQtyFocusOut) ----
   // Focusing an EXISTING leg's qty splits the cell: the original qty becomes read-only and a second box
   // takes the DESIRED REMAINING magnitude. New legs keep the single editable box (HG parity — the split
   // is an owned-leg affordance only).

   @DetectMethodChanges()
   onQtyFocusIn(leg: MtEditableLeg): void {
      if (leg.isNew || leg.crossedOut || leg.qtySplit) { return; }
      leg.qtySplit = true;
      leg.transQty = undefined;
   }

   // Auto-focus the just-rendered split box (HG onQtyBoxInit clone).
   onTransQtyBoxInit(box: DxNumberBoxComponent): void {
      setTimeout(() => box.instance.focus());
   }

   // Split-box keystrokes: store the raw value; commit valid (> 0) magnitudes to the end-state qty the
   // diff reads. Invalid values are NOT toasted per keystroke — focus-out settles them (HG parity).
   @DetectMethodChanges()
   onTransQtyChange(leg: MtEditableLeg, value: number): void {
      leg.transQty = value;
      if (this.isPositiveNumber(value)) {
         leg.targetQty = value;
         leg.qty = value;
      }
   }

   // Focus-out settles the split: an invalid/empty transQty reverts the leg to its original qty and closes
   // the split (with a toast only when the operator actually typed something invalid — HG parity).
   @DetectMethodChanges()
   onQtyFocusOut(leg: MtEditableLeg): void {
      if (this.isPositiveNumber(leg.transQty)) { return; }
      if (leg.transQty !== null && leg.transQty !== undefined) {
         this._toastr.error('Transaction Qty Is Not A Valid Number');
      }
      leg.qtySplit = false;
      leg.transQty = undefined;
      leg.targetQty = leg.originalQty;
      leg.qty = leg.originalQty;
   }

   private isPositiveNumber(value: any): boolean {
      return typeof value === 'number' && isFinite(value) && value > 0;
   }

   // User-facing instrument name (owner rule 2026-06-10: UIs NEVER show a raw ticker — only the display
   // name; stocks exempt since raw == display). Same convention as collars.component dispName(): option
   // code -> Collars friendly name; asset code -> registered display name (falls back to the code itself).
   legDisplayName(code: string): string {
      if (!code) { return ''; }
      const t = parseOptionTicker(code);
      if (t) {
         const ulName = this._displayNames.getDisplayNameForTicker(t.underlying);
         return makeCollarsOptionDisplayName(t, ulName);
      }
      return this._displayNames.getDisplayNameForTicker(code) || code;
   }

   // Side-flip (Buy <-> Sell). The colored select emits the new DISPLAY string; map it to a sign. An EXISTING
   // leg flips via clone-and-remove (cross original + add a normal new opposite-side leg, NO merge). A
   // brand-new leg flips in place.
   @DetectMethodChanges()
   onLegSideChanging(leg: MtEditableLeg, value: string): void {
      const nextSide = value === 'Sell' ? -1 : 1;
      if (leg.crossedOut) { return; }
      if (leg.side === nextSide) { return; }
      if (leg.isNew) {
         leg.side = nextSide;
         return;
      }
      // Existing leg: cross the original and add a NORMAL new editable leg carrying the original's fixed
      // instrument with the flipped side. The diff later splits this into Close(current) + Open(opposite).
      // Clone BEFORE settling so a committed qty-split's desired magnitude carries onto the flipped leg;
      // the crossed original then returns to face value (it prices/books the FULL close).
      leg.crossedOut = true;
      const flipped = this.makeRolledLeg(leg, { side: nextSide });
      this.settleQtySplit(leg);
      this.insertAfter(leg, flipped);
      this.syncLegQuote(flipped);
   }

   // Strike change on a NEW option leg (symbol-picker chain). Edit in place; re-resolve the quote.
   @DetectMethodChanges()
   onStrikeChange(leg: MtEditableLeg, value: number): void {
      if (isVoid(value)) { return; }
      if (leg.strike === value) { return; }
      leg.strike = value;
      this.refreshNewLegCode(leg);
      this.syncLegQuote(leg);
   }

   // Strike change on an EXISTING option leg (inline strikes-dropdown). Its instrument is immutable, so roll
   // it (clone-and-remove): cross the original + add a NORMAL new editable leg at the new strike.
   @DetectMethodChanges()
   onExistingStrikeChange(leg: MtEditableLeg, value: number): void {
      if (isVoid(value)) { return; }
      if (leg.strike === value) { return; }
      this.rollExistingLeg(leg, { strike: value });
   }

   // Expiration change on a NEW option leg (picker emits an OptionExpirationDescriptor). Re-resolve the quote.
   @DetectMethodChanges()
   onExpiryChange(leg: MtEditableLeg, value: any): void {
      if (leg.expiration === value) { return; }
      leg.expiration = value;
      // Keep the leg's own underlying in sync with the picked descriptor (rolled / picker legs alike).
      if (value && (value as OptionExpirationDescriptor).underlyingTicker) {
         leg.underlyingSymbol = (value as OptionExpirationDescriptor).underlyingTicker;
         leg.expirationDescriptor = value as OptionExpirationDescriptor;
      }
      this.refreshNewLegCode(leg);
      this.syncLegQuote(leg);
   }

   // Expiration change on an EXISTING option leg (inline expiration-selector emits an OptionExpirationDescriptor
   // from the leg's OWN underlying chain). Roll it (clone-and-remove). No-op when the picked descriptor
   // resolves to the same expiration date the leg already holds.
   @DetectMethodChanges()
   onLegExpirationChanging(leg: MtEditableLeg, descriptor: OptionExpirationDescriptor): void {
      if (isVoid(descriptor)) { return; }
      const newDate = descriptor.optionExpirationDate;
      if (isVoid(newDate) || newDate === leg.expiration) { return; }
      this.rollExistingLeg(leg, { expirationDescriptor: descriptor });
   }

   // Type change (Call <-> Put). A NEW leg changes type in place + re-resolves the quote. An EXISTING leg
   // rolls via clone-and-remove (cross original + add a NORMAL new editable leg with the new type), exactly
   // like strike/expiry — its instrument is immutable.
   @DetectMethodChanges()
   onOptionTypeChange(leg: MtEditableLeg, value: string): void {
      if (isVoid(value)) { return; }
      if (leg.optionType === value) { return; }
      if (leg.isNew) {
         leg.optionType = value;
         this.refreshNewLegCode(leg);
         this.syncLegQuote(leg);
         return;
      }
      this.rollExistingLeg(leg, { optionType: value });
   }

   // CLONE-AND-REMOVE for an EXISTING option leg's strike/expiry (the HedgePad roll primitive). Cross out the
   // original (restorable) and push a NORMAL new editable leg seeded with the original's underlying + type +
   // qty + side + group and the UNCHANGED attribute, applying the CHANGED one, composing a VALID ticker via
   // makeOptionTicker against the leg's OWN underlying chain descriptor. The rolled leg is just a normal new
   // editable leg (no clonedFromId / supersedes binding) — the operator can keep editing it.
   private rollExistingLeg(leg: MtEditableLeg, change: { strike?: number; expirationDescriptor?: OptionExpirationDescriptor; optionType?: string }): void {
      const descriptor = change.expirationDescriptor || leg.expirationDescriptor
         || this.resolveDescriptor(leg.underlyingSymbol, leg.expiration);
      if (isVoid(descriptor)) {
         this._toastr.error('Cannot Roll This Leg Yet — Its Option Chain Is Still Loading');
         return;
      }
      const strike = !isVoid(change.strike) ? change.strike : leg.strike;
      if (isVoid(strike) || strike <= 0) {
         this._toastr.error('A Rolled Leg Needs a Strike');
         return;
      }
      const optionType = !isVoid(change.optionType) ? change.optionType : leg.optionType;
      const code = makeOptionTicker(descriptor, optionType, strike);
      if (isVoid(code)) {
         this._toastr.error('Could Not Compose the Rolled Option Ticker');
         return;
      }
      // Clone BEFORE settling (the rolled leg carries a committed qty-split's desired magnitude); the
      // crossed original returns to face value — it prices/books the FULL close.
      leg.crossedOut = true;
      const rolled = this.makeRolledLeg(leg, {
         strike,
         optionType,
         expiration: descriptor,
         expirationDescriptor: descriptor,
         underlyingSymbol: descriptor.underlyingTicker || leg.underlyingSymbol,
         tradingInstrumentCode: code
      });
      this.settleQtySplit(leg);
      this.insertAfter(leg, rolled);
      this.syncLegQuote(rolled);
   }

   // ---- Add legs (new legs route to a leaf group; only GROUPS hold positions) ----

   @DetectMethodChanges()
   onAddOption(): void {
      if (!this.ensureSingleUnderlyingForAdd()) { return; }
      this.editableLegs = this.editableLegs.concat(this.makeNewLeg(KIND_OPTION));
   }

   @DetectMethodChanges()
   onAddAsset(): void {
      if (!this.ensureSingleUnderlyingForAdd()) { return; }
      const leg = this.makeNewLeg(KIND_ASSET);
      this.editableLegs = this.editableLegs.concat(leg);
      // An asset leg is complete at birth — no strike/expiry/type edit will ever fire syncLegQuote for it
      // (the way option legs get theirs), so wire its reference quote NOW. Without this the leg never
      // subscribes and the price box stays blank whenever the symbol was already set before Add Asset.
      this.syncLegQuote(leg);
   }

   // A new leg is routed to a leaf group. When the scope is a Group, that is the target. Otherwise route to
   // the first available target Group under the scope; if none, leave empty (validation blocks Save/Commit).
   private makeNewLeg(kind: number): MtEditableLeg {
      const groupBucketId = this.defaultNewLegGroup();
      const qty = kind === KIND_ASSET ? this.defaultAssetQty() : 1;
      return {
         id: ++this._seq,
         isNew: true,
         crossedOut: false,
         restorable: false,
         groupBucketId,
         kind,
         tradingInstrumentCode: '',
         side: 1,
         qty,
         originalQty: 0,
         targetQty: qty,
         optionType: kind === KIND_OPTION ? 'Put' : undefined,
         strike: undefined,
         expiration: undefined,
         underlyingSymbol: undefined,
         expirationDescriptor: undefined,
         liveQuote: undefined
      };
   }

   private defaultNewLegGroup(): string {
      if (this._config.level === LEVEL_GROUP) { return this._config.scopeBucketId; }
      const groups = this.routingTargetGroups;
      if (groups.length === 1) { return groups[0].bucketId; }
      const firstWithGroup = this.editableLegs.find(l => !isVoid(l.groupBucketId));
      return firstWithGroup ? firstWithGroup.groupBucketId : '';
   }

   // Default qty for a NEW asset leg (owner rule 2026-06-11): a stock defaults to a round lot of 100
   // shares; a FUTURES underlying stays at 1 contract. Decided at add time from the symbol picker's
   // instrument; no symbol picked yet -> stock assumption (100). A starting value only — the operator
   // edits the qty box freely; a later symbol pick does NOT rewrite it (it could clobber a typed qty).
   private defaultAssetQty(): number {
      const inst = this.underlying;
      return (inst && inst.kind === TradingInstrumentKind.Futures) ? 1 : 100;
   }

   // Target Groups for a leg's routing picker: the leaf Groups inside the opened scope (Interest and
   // archived groups excluded — an archived bucket is read-only; the backend rejects fills into it).
   get routingTargetGroups(): CollarsBucketDto[] {
      const id = this._config.scopeBucketId;
      const level = this._config.level;
      const groups = this._buckets.filter(b => b.level === LEVEL_GROUP && b.name !== 'Interest' && !b.isArchived);
      if (level === LEVEL_GROUP) {
         return groups.filter(g => g.bucketId === id);
      }
      if (level === LEVEL_COMBO) {
         return groups.filter(g => g.parentBucketId === id);
      }
      const combosOfPortfolio = this._buckets
         .filter(b => b.level === LEVEL_COMBO && b.parentBucketId === id)
         .map(b => b.bucketId);
      return groups.filter(g => combosOfPortfolio.indexOf(g.parentBucketId) >= 0);
   }

   // Build a NORMAL new editable leg from an existing leg (side-flip / roll). It carries a FIXED, pre-composed
   // instrument code (set by the caller) so codeOf returns it directly; it is otherwise a regular new leg the
   // operator can keep editing.
   private makeRolledLeg(src: MtEditableLeg, overrides: Partial<MtEditableLeg>): MtEditableLeg {
      return {
         id: ++this._seq,
         isNew: true,
         crossedOut: false,
         restorable: false,
         portfolioId: src.portfolioId,
         comboId: src.comboId,
         groupBucketId: src.groupBucketId,
         kind: src.kind,
         tradingInstrumentCode: src.tradingInstrumentCode,
         side: src.side,
         qty: src.qty,
         originalQty: 0,
         targetQty: src.qty,
         optionType: src.optionType,
         strike: src.strike,
         expiration: src.expiration,
         underlyingSymbol: src.underlyingSymbol,
         expirationDescriptor: src.expirationDescriptor,
         liveQuote: src.liveQuote,
         ...overrides
      };
   }

   private insertAfter(anchor: MtEditableLeg, leg: MtEditableLeg): void {
      const ix = this.editableLegs.indexOf(anchor);
      const next = this.editableLegs.slice();
      if (ix < 0) { next.push(leg); } else { next.splice(ix + 1, 0, leg); }
      this.editableLegs = next;
   }

   // ---- Leg routing picker change ----

   @DetectMethodChanges()
   onLegGroupChange(leg: MtEditableLeg, groupBucketId: string): void {
      leg.groupBucketId = groupBucketId;
   }

   // ---- Underlying + reference quotes ----

   // Symbol picker emits a TradingInstrument; load its option chain for the expiration list and re-resolve
   // every NEW picker leg's reference-quote subscription against the new underlying.
   @DetectMethodChanges({ isAsync: true })
   async onSymbolSelected(instrument: TradingInstrument): Promise<void> {
      this.underlying = instrument;
      this.expirationsList = [];
      if (instrument) {
         try {
            const chain = await this._optionChains.getChain(instrument.ticker);
            this.expirationsList = (chain && chain.expirations) ? chain.expirations.slice() : [];
         } catch (e) {
            this._logger.error('Collars make-transaction: load expirations failed', { error: e.stack || e });
            this._toastr.error('Failed to Load Expirations');
         }
      }
      // Re-resolve only NEW picker legs (those WITHOUT their own underlying); rolled legs keep their own.
      this.editableLegs.forEach(l => { if (l.isNew && isVoid(l.underlyingSymbol)) { this.syncLegQuote(l); } });
   }

   // Indexes (e.g. SPX) are not tradable as an asset leg — only options on them. Block Add Asset once an
   // index underlying is selected; allowed when no underlying yet or a non-index is chosen.
   // Also blocked once a SURVIVING asset leg exists: the end state holds at most ONE asset leg (owner
   // rule 2026-06-10). A crossed-out asset doesn't count — replacing it stays possible.
   get canAddAsset(): boolean {
      const inst = this.underlying;
      if (inst && inst.kind === TradingInstrumentKind.Index) { return false; }
      return !this.editableLegs.some(l => l.kind === KIND_ASSET && !l.crossedOut);
   }

   // Resolve a leg's reference-quote ticker and diff-subscribe via the shared quote cache. No-op when the
   // ticker is unchanged.
   private syncLegQuote(leg: MtEditableLeg): void {
      const next = this.legQuoteTicker(leg);
      const prev = leg.quoteTicker;
      if (next === prev) { return; }
      if (prev) { this._lastQuoteCache.unsubscribeTicker(prev); }
      leg.quoteTicker = next;
      leg.liveQuote = undefined;
      if (next) {
         const q = this._lastQuoteCache.subscribeTicker(next);
         leg.liveQuote = this.pickQuote(q, leg);
      }
   }

   // EXISTING leg, or a NEW leg that already carries a fixed (rolled) code → that code. GENUINELY new asset
   // leg → underlying ticker; new option leg → composed option ticker once expiry/type/strike are set.
   //
   // EXISTING OPTION leg: the live quote bus keys options by the ticker makeOptionTicker() composes from the
   // CHAIN descriptor (authoritative expirationTicker root, e.g. @SPXW). The stored tradingInstrumentCode is
   // NOT guaranteed byte-identical to that (positions can originate from TOS copy, retro-fill, transfers or
   // expiry settlement, not only the blotter), so subscribing it verbatim can miss every tick → no quote.
   // Recompose from the resolved expirationDescriptor exactly like the blotter (collars.component.ts
   // legQuoteTicker), and fall back to the stored code only when the descriptor has not resolved yet. ASSET
   // legs keep their stored underlying code (it IS the bus ticker). Booking is unaffected — it reads
   // tradingInstrumentCode via codeOf, never this quote ticker.
   private legQuoteTicker(leg: MtEditableLeg): string {
      if (!leg.isNew) {
         if (leg.kind === KIND_OPTION && leg.expirationDescriptor && !isVoid(leg.optionType) && !isVoid(leg.strike)) {
            const composed = makeOptionTicker(leg.expirationDescriptor, leg.optionType, leg.strike);
            if (!isVoid(composed)) { return composed; }
         }
         return leg.tradingInstrumentCode || null;
      }
      if (!isVoid(leg.tradingInstrumentCode) && String(leg.tradingInstrumentCode).trim()) {
         return leg.tradingInstrumentCode;
      }
      const inst = this.underlying;
      if (!inst) { return null; }
      if (leg.kind === KIND_ASSET) { return inst.ticker; }
      if (leg.expiration && leg.optionType && leg.strike) {
         return makeOptionTicker(leg.expiration, leg.optionType, leg.strike) || null;
      }
      return null;
   }

   // Reference-price convention: option legs show MID, asset legs show LAST.
   private pickQuote(q: QuoteDto, leg: MtEditableLeg): number {
      if (!q) { return undefined; }
      if (leg.kind === KIND_OPTION) {
         return (typeof q.mid === 'number') ? q.mid : undefined;
      }
      return (typeof q.lastPx === 'number') ? q.lastPx : undefined;
   }

   // Live reference quotes arrive on the QuoteDto bus. Update each leg whose subscribed ticker matches.
   @DetectMethodChanges()
   private onQuotes(quotes: QuoteDto[]): void {
      if (!this.isVisible || !quotes || quotes.length === 0) { return; }
      for (let i = 0; i < quotes.length; i++) {
         const q = quotes[i];
         this.editableLegs.forEach(leg => {
            if (leg.quoteTicker && leg.quoteTicker === q.ticker) {
               leg.liveQuote = this.pickQuote(q, leg);
            }
         });
      }
   }

   private dropLegQuote(leg: MtEditableLeg): void {
      if (leg.quoteTicker) {
         this._lastQuoteCache.unsubscribeTicker(leg.quoteTicker);
         leg.quoteTicker = null;
      }
   }

   private unsubscribeAllLegQuotes(): void {
      this.editableLegs.forEach(leg => this.dropLegQuote(leg));
   }

   // ---- Preview (separate, PRICE-FREE pop-up) ----

   // Build the SAME diff that backs Apply After State, map it to the preview's row model, and open the
   // separate price-free Before/Transaction/After pop-up. The preview contains no price inputs.
   // VALIDATED like the committing actions (owner ruling 2026-06-10): a duplicate/invalid leg set must not
   // reach the preview either — the diff would silently collapse duplicate (group, instrument) lines and
   // show a misleading picture.
   //
   // OWNER WORKFLOW (2026-06-10): design -> Preview -> from the preview either Copy to TOS or Close.
   // Copy to TOS is the committing action: it writes the orders to the OS clipboard AND persists the
   // end-state (the standalone Save button was removed; its functionality lives here now).
   @DetectMethodChanges({ isAsync: true })
   async onPreview(): Promise<void> {
      const survivors = this.survivingLegs();
      if (!this.validateSurvivors(survivors)) { return; }
      this.composeNewLegCodes();
      const diff = this._diff.computeDiff(
         this._currentPositions,
         this.editableLegs,
         this._config.scopeBucketId,
         this._config.level
      );
      if (!diff.isValid) {
         this._toastr.error(diff.blockReason || 'Transaction Is Not Valid');
         return;
      }
      const rows = toAfterStateRows(diff, this.afterStateRowsContext());
      if (!this.previewDialog) { return; }

      const result = await this.previewDialog.show(rows);
      if (result.action === 'copy-tos') {
         await this.copyToTosAndPersist();
      } else if (result.action === 'acquire') {
         // Acquire: the review's ACQUISITION mode collected one fill price per transaction leg in-place
         // (no interface switch); book them now.
         await this.bookAcquisition(result.fills || []);
      }
   }

   // The preview's Copy-to-TOS: clipboard write FIRST, then persist the end-state, then ONE success toast
   // and close (the owner-stated order: copy -> success -> save). A failed copy leaves the dialog open and
   // saves nothing; a failed save leaves the dialog open too (the clipboard already holds the orders).
   private async copyToTosAndPersist(): Promise<void> {
      this.busy = true;
      this._changeDetector.detectChanges();
      try {
         const copy = await this.copyOrdersToTos();
         if (!copy.ok) { return; }

         const saved = await this.persistEndState();
         if (!saved) { return; }

         const skipNote = copy.skippedAssetLegs > 0
            ? ` (${copy.skippedAssetLegs} asset leg(s) skipped — enter the stock order manually)`
            : '';
         this._toastr.success(`Copied to TOS${skipNote} — End-State Saved`);
         this.unsubscribeAllLegQuotes();
         this.isVisible = false;
         this.resolveWith({ saved: true, committed: false });
      } finally {
         this.busy = false;
         this._changeDetector.detectChanges();
      }
   }

   // Display context for the preview rows: bucket-id -> NAME (the section titles must show group names,
   // never raw ids) and group -> parent combo (drives the combo separators at portfolio scope). Built from
   // the bucket tree already loaded on show() — no extra round-trip.
   private afterStateRowsContext(): CollarsAfterStateRowsContext {
      const names: { [bucketId: string]: string } = {};
      const combos: { [groupId: string]: { comboBucketId: string; comboName: string } } = {};
      const byId: { [bucketId: string]: CollarsBucketDto } = {};
      this._buckets.forEach(b => { byId[b.bucketId] = b; names[b.bucketId] = b.name; });
      this._buckets.forEach(b => {
         if (b.level !== LEVEL_GROUP) { return; }
         const combo = b.parentBucketId ? byId[b.parentBucketId] : null;
         if (combo) {
            combos[b.bucketId] = { comboBucketId: combo.bucketId, comboName: combo.name };
         }
      });
      return { groupNames: names, groupCombos: combos };
   }

   // ---- Save ----

   // Persist the END-STATE legs per CHANGED leaf group only (one settings key each); nothing is booked.
   // CHANGED = the diff derives at least one delta for the group. Persisting every survivor group (the
   // old behavior) littered keys onto untouched buckets: Apply skips zero-delta groups and deletes only
   // booked groups' keys, so those useless keys lived forever as ghost green badges on buckets the plan
   // never touched. A changed group with ZERO survivors (full close) saves an EMPTY-legs plan. Also
   // RETIRES obsolete in-scope keys (groups that no longer differ under the current plan).
   //
   // PERSISTENCE CORE ONLY — no validation/compose (the caller already ran them), no busy handling, no
   // closing, no success toast: this is the SAVE functionality now produced by the preview's Copy-to-TOS
   // (the standalone Save button was removed — owner workflow 2026-06-10). Returns success.
   private async persistEndState(): Promise<boolean> {
      const survivors = this.survivingLegs();
      const diff = this._diff.computeDiff(
         this._currentPositions, this.editableLegs, this._config.scopeBucketId, this._config.level
      );
      if (!diff.isValid) {
         this._toastr.error(diff.blockReason || 'Transaction Is Not Valid');
         return false;
      }
      const changedGroupIds: string[] = [];
      (diff.transaction || []).forEach(g => {
         if ((g.deltas || []).length > 0 && changedGroupIds.indexOf(g.groupBucketId) < 0) {
            changedGroupIds.push(g.groupBucketId);
         }
      });

      try {
         const byGroup = this.groupSurvivors(survivors);
         for (let i = 0; i < changedGroupIds.length; i++) {
            const groupId = changedGroupIds[i];
            const groupLegs = byGroup[groupId] || [];   // [] = the plan closes the whole group
            const legs = groupLegs.map(l => ({
               groupBucketId: groupId,
               tradingInstrumentCode: this.codeOf(l),
               kind: l.kind,
               side: l.side,
               qty: Math.abs(l.qty),
               optionType: l.optionType,
               strike: l.strike,
               // Persist the expiration DATE STRING, never the picker's OptionExpirationDescriptor object —
               // rolled/new legs hold the descriptor in `expiration`, and a stored object breaks the
               // preview/apply date rendering and bloats the settings payload.
               expiration: this.expirationIsoOf(l)
            }));
            await this._afterState.saveAfterState(groupId, this._config.terminalId, legs);
         }

         // Retire obsolete plans: in-scope groups holding a key but UNCHANGED under this save — their old
         // plan no longer reflects the operator's intent (this save IS the current intent for the scope).
         const scopeGroupIds = this.routingTargetGroups.map(g => g.bucketId);
         const saved = this._afterState.listAfterStateGroupIds();
         let retired = 0;
         for (let j = 0; j < saved.length; j++) {
            const id = saved[j];
            if (scopeGroupIds.indexOf(id) >= 0 && changedGroupIds.indexOf(id) < 0) {
               await this._afterState.discard(id);
               retired++;
            }
         }

         this._logger.debug(
            `Collars make-transaction SAVE ok -> scope=${this._config.scopeBucketId} ` +
            `level=${this._config.level} changedGroups=${changedGroupIds.length} retired=${retired}`);
         return true;
      } catch (e) {
         this._logger.error('Collars make-transaction save failed', { error: e.stack || e });
         this._toastr.error('Save failed');
         return false;
      }
   }

   // ---- Acquisition (prices collected IN the review pop-up's acquisition mode) ----

   // Book the priced transaction (one RecordCollarsTransaction per fill) in the supplied order —
   // close-before-open per instrument holds because the fills follow the diff's per-group emission order.
   // Saved after-state keys are NOT touched: a direct acquisition books now; it is not an Apply.
   private async bookAcquisition(fills: CollarsAcquireFill[]): Promise<void> {
      if (fills.length === 0) {
         this._toastr.info('No change to book');
         return;
      }
      this.busy = true;
      this._changeDetector.detectChanges();
      let booked = 0;
      try {
         // A combo under analysis (CR-20260930-001) takes no manual fill: say why before sending anything —
         // the Shell would refuse each leg, and a refused command carries no text over WCF.
         const groupIds = fills.map(f => f.groupBucketId).filter((g, i, a) => !!g && a.indexOf(g) === i);
         const lockReason = await this._analysisLocks.reasonFor(groupIds);
         if (lockReason) {
            this._toastr.error(lockReason);
            return;
         }

         for (let i = 0; i < fills.length; i++) {
            const f = fills[i];
            await this._shellClient.processCommand(new RecordCollarsTransaction(
               this._config.terminalId,
               f.groupBucketId,
               f.kind,
               f.tradingInstrumentCode,
               Math.abs(f.qty),
               f.side,
               f.fillPx,
               null
            ));
            booked++;
         }
         this._logger.info(`Collars make-transaction ACQUIRE ok -> scope=${this._config.scopeBucketId} legs=${fills.length}`);
         this._toastr.success('Transaction booked');
         this.unsubscribeAllLegQuotes();
         this.isVisible = false;
         this.resolveWith({ saved: false, committed: true });
      } catch (e) {
         this._logger.error('Collars make-transaction acquisition failed', { error: e.stack || e, booked });
         if (booked > 0) {
            // PARTIAL booking: the dialog's loaded positions are now STALE — another Review would re-derive
            // (and re-book) the legs that already succeeded. Close as committed so the panel reloads the
            // real book; the operator re-enters with fresh positions.
            this._toastr.error(
               `Acquisition failed after booking ${booked} of ${fills.length} legs — ` +
               `check the ledger, then make a new transaction for the remainder`);
            this.unsubscribeAllLegQuotes();
            this.isVisible = false;
            this.resolveWith({ saved: false, committed: true });
         } else {
            this._toastr.error('Acquisition failed — nothing was booked');
         }
      } finally {
         this.busy = false;
         this._changeDetector.detectChanges();
      }
   }

   // ---- Copy to TOS (reached from the PREVIEW pop-up; the committing half is copyToTosAndPersist) ----

   // Derive the transaction and push its legs to ThinkOrSwim via the adapter + CopyOrdersToService.
   // Blocks/failures toast HERE and return ok=false; SUCCESS does not toast — the orchestrator shows the
   // single combined "copied + saved" toast.
   private async copyOrdersToTos(): Promise<{ ok: boolean; skippedAssetLegs: number }> {
      const failed = { ok: false, skippedAssetLegs: 0 };
      if (isVoid(this._copyOrders)) {
         this._toastr.error('Copy to TOS is unavailable (CopyOrdersToService not provided)');
         return failed;
      }
      const survivors = this.survivingLegs();
      if (!this.validateSurvivors(survivors)) { return failed; }
      this.composeNewLegCodes();
      const result = this._diff.computeDiff(
         this._currentPositions, this.editableLegs, this._config.scopeBucketId, this._config.level
      );
      if (!result.isValid) {
         this._toastr.error(result.blockReason || 'Transaction Is Not Valid');
         return failed;
      }
      const deltas = this.flattenDeltasFrom(result);
      if (deltas.length === 0) {
         this._toastr.info('No legs to copy');
         return failed;
      }
      // The TOS-Desktop order builder composes OPTION orders only — an asset/stock leg has no
      // type/strike/expiration and would throw inside the builder, killing the whole copy. The adapter
      // drops asset deltas; the caller reports how many were skipped so the stock order is entered by hand.
      const skippedAssetLegs = CollarsCopyToTosAdapter.countAssetLegs(deltas);
      if (skippedAssetLegs === deltas.length) {
         this._toastr.warning('Nothing copied — TOS copy covers option legs only, and this transaction has none');
         return failed;
      }
      // ONE underlying per TOS order: the desktop line builder stamps the whole CUSTOM order with the
      // FIRST leg's underlying and has no mixed-underlying guard — legs of other underlyings would be
      // silently mislabeled (APG never mixes; Collars scopes can).
      const optionUnderlyings: string[] = [];
      deltas.forEach(d => {
         if (d.kind !== KIND_OPTION) { return; }
         const parsed = parseOptionTicker(d.tradingInstrumentCode);
         const ul = parsed ? parsed.underlying : null;
         if (ul && optionUnderlyings.indexOf(ul) < 0) { optionUnderlyings.push(ul); }
      });
      if (optionUnderlyings.length > 1) {
         this._toastr.error(
            'TOS copy supports ONE underlying per order — this transaction spans ' +
            optionUnderlyings.join(', ') + '. Copy each underlying separately.');
         return failed;
      }
      try {
         const prices = this.livePxByCode(deltas);
         const carrier = CollarsCopyToTosAdapter.toCopyOrdersInput(deltas, prices);
         // copyToTOSDesktop writes the TOS-Desktop order text to the OS clipboard (navigator.clipboard).
         // The old copyToETS only fills the INTERNAL ETS clipboard (panel-to-panel) — nothing reaches the OS
         // clipboard, which is why it "succeeded" but pasted nothing into ThinkOrSwim.
         const ok = await this._copyOrders.copyToTOSDesktop(carrier, 'All', 1);
         if (!ok) {
            this._toastr.warning('Nothing copied — could not build the TOS order');
            return failed;
         }
         return { ok: true, skippedAssetLegs };
      } catch (e) {
         this._logger.error('Collars make-transaction copy-to-TOS failed', { error: e.stack || e });
         this._toastr.error('Copy to TOS failed');
         return failed;
      }
   }

   // ---- View helpers ----

   // The legs are rendered in two passes: EXISTING (loaded, !isNew) first, then a divider, then NEW (isNew).
   // A rolled/flipped clone is a NEW leg, so it lands in the new group below the divider. Within EACH group the
   // rows are sorted for display: ASSETS first, then OPTIONS by STRIKE DESCENDING (highest strike on top).
   get orderedExistingLegs(): MtEditableLeg[] {
      return this.sortForDisplay(this.editableLegs.filter(l => !l.isNew));
   }

   get orderedNewLegs(): MtEditableLeg[] {
      return this.sortForDisplay(this.editableLegs.filter(l => l.isNew));
   }

   // Display order within a section: assets first, then options CLUSTERED BY UNDERLYING (alphabetical —
   // deterministic; single-underlying scopes are unaffected), within an underlying by strike DESCENDING.
   // The template renders a separator between underlying clusters (vrequest 2026-06-10 #4 item 1). Stable
   // on ties (keeps the original relative order) since Array.sort is not guaranteed stable on ES5.
   private sortForDisplay(legs: MtEditableLeg[]): MtEditableLeg[] {
      return legs
         .map((leg, i) => ({ leg: leg, i: i }))
         .sort((a, b) => {
            const aAsset = a.leg.kind === KIND_ASSET;
            const bAsset = b.leg.kind === KIND_ASSET;
            if (aAsset !== bAsset) { return aAsset ? -1 : 1; }
            if (!aAsset) {
               // 0) underlying ASC — options cluster per underlying; separators render between clusters
               const ua = this.legUnderlyingKey(a.leg);
               const ub = this.legUnderlyingKey(b.leg);
               if (ua !== ub) { return ua < ub ? -1 : 1; }
               // 1) strike DESC (highest on top)
               const sa = (a.leg.strike != null) ? a.leg.strike : -Infinity;
               const sb = (b.leg.strike != null) ? b.leg.strike : -Infinity;
               if (sb !== sa) { return sb - sa; }
               // 2) expiration ASC (nearest first) — ISO yyyy-MM-dd sorts lexicographically = chronologically
               const ea = this.legExpirationSortKey(a.leg);
               const eb = this.legExpirationSortKey(b.leg);
               if (ea !== eb) { return ea < eb ? -1 : 1; }
               // 3) option type: Call before Put
               const ta = this.isCall(a.leg) ? 0 : 1;
               const tb = this.isCall(b.leg) ? 0 : 1;
               if (ta !== tb) { return ta - tb; }
            }
            return a.i - b.i;
         })
         .map(x => x.leg);
   }

   // The underlying an OPTION leg clusters under: its own resolved underlying (existing/rolled legs), or
   // the symbol picker's for a genuinely-new picker leg. OPTIONS-ONLY — both call sites (the sort tiebreak
   // and isUnderlyingBreak) exclude asset legs before calling; assets keep their original order and never
   // get separators.
   private legUnderlyingKey(leg: MtEditableLeg): string {
      if (!isVoid(leg.underlyingSymbol)) { return leg.underlyingSymbol; }
      return this.underlying ? this.underlying.ticker : '';
   }

   // ---- Underlying lock + uniformity (owner rule 2026-06-10: ONE underlying per transaction) ----

   // The underlying is DETERMINED — and the Symbol picker LOCKS — once any leg is bound to it: scope legs
   // always are (their instruments name it); new picker legs bind the moment a symbol is chosen. An empty
   // dialog, or legs added before any symbol pick, keep the picker enabled — nothing is determined yet.
   get underlyingLocked(): boolean {
      const legs = this.editableLegs;
      for (let i = 0; i < legs.length; i++) {
         if (!legs[i].isNew) { return true; }
      }
      return legs.length > 0 && !isVoid(this.underlying);
   }

   // Distinct underlying keys across the SURVIVING legs (crossed-out legs leave the package). Asset legs
   // key by their own code; unbound picker legs (no symbol yet) contribute nothing.
   private dialogUnderlyingKeys(): string[] {
      const keys: string[] = [];
      this.editableLegs.forEach(l => {
         if (l.crossedOut) { return; }
         const key = l.kind === KIND_ASSET
            ? this.chainKeyFor(l.tradingInstrumentCode)
            : this.legUnderlyingKey(l);
         if (!isVoid(key) && String(key).trim() && keys.indexOf(key) < 0) { keys.push(key); }
      });
      return keys;
   }

   // Pre-add guard: a new leg must never join a package whose legs already span more than one underlying,
   // or contradict the picked symbol. With the picker locked this holds by construction — this is the
   // backstop (e.g. a legacy mixed-underlying scope).
   private ensureSingleUnderlyingForAdd(): boolean {
      const keys = this.dialogUnderlyingKeys();
      if (keys.length > 1) {
         this._toastr.error(`One underlying per transaction — this package already spans ${keys.join(', ')}`);
         return false;
      }
      if (keys.length === 1 && !isVoid(this.underlying)) {
         const pickerKey = this.chainKeyFor(this.underlying.ticker);
         if (pickerKey !== keys[0]) {
            this._toastr.error(`One underlying per transaction — the package is ${keys[0]}, the picker shows ${pickerKey}`);
            return false;
         }
      }
      return true;
   }

   // A separator renders ABOVE a leg row when it starts a NEW underlying cluster among the option legs
   // (both neighbours options, different underlyings). Called from the template per row — leg lists are
   // small, the repeated getter evaluation is cheap.
   isUnderlyingBreak(legs: MtEditableLeg[], ix: number): boolean {
      if (ix <= 0 || ix >= legs.length) { return false; }
      const prev = legs[ix - 1];
      const cur = legs[ix];
      if (prev.kind !== KIND_OPTION || cur.kind !== KIND_OPTION) { return false; }
      return this.legUnderlyingKey(prev) !== this.legUnderlyingKey(cur);
   }

   // Asset isolation (owner ruling 2026-06-10, refined): assets sort first within a section; the boundary
   // between the asset cluster and the option legs renders the SAME solid separator used between the
   // existing and new sections (no frame/background on the asset rows themselves).
   isAssetOptionBreak(legs: MtEditableLeg[], ix: number): boolean {
      if (ix <= 0 || ix >= legs.length) { return false; }
      return legs[ix - 1].kind === KIND_ASSET && legs[ix].kind === KIND_OPTION;
   }

   // A sortable expiration key. Prefers the resolved descriptor's ISO optionExpirationDate (yyyy-MM-dd, which
   // sorts lexicographically = chronologically); falls back to a new-leg descriptor or the raw stored string.
   private legExpirationSortKey(leg: MtEditableLeg): string {
      const d: any = leg.expirationDescriptor;
      if (d && d.optionExpirationDate) { return String(d.optionExpirationDate); }
      const e: any = leg.expiration;
      if (e && typeof e === 'object' && e.optionExpirationDate) { return String(e.optionExpirationDate); }
      if (typeof e === 'string') { return e; }
      return '';
   }

   private isCall(leg: MtEditableLeg): boolean {
      return String(leg.optionType || '').toLowerCase() === 'call';
   }

   isOption(leg: MtEditableLeg): boolean {
      return leg.kind === KIND_OPTION;
   }

   sideText(side: number): string {
      return side === 1 ? 'Buy' : 'Sell';
   }

   // The colored Buy/Sell input style (HG idiom: cornflowerblue for Buy, red for Sell), applied via inputAttr.
   sideStyle(side: number): string {
      return side === 1 ? 'text-align: center; color: cornflowerblue;' : 'text-align: center; color: red;';
   }

   // ---- Own / Transaction pricing (HG HedgePad clone: getLegPrice / pricebox visibility / totals) ----
   //
   // OWN price = the leg priced AS IF OWNED: sign = the assumed CLOSING action. Long -> close by selling ->
   // positive (receive premium); short -> close by buying back -> negative (pay). TRANSACTION price = the
   // DIRECT price of the derived action: buy -> negative (pay); sell -> positive (receive). The raw live
   // reference quote supplies the magnitude; sign/colour reflect the action (HG semantics, narrated in the
   // vrequest 2026-06-10 recording).

   // The price area splits into two sub-columns as soon as ANY transaction exists (new leg, cross-out, or a
   // qty split) — HG getTransPriceboxDisplay analog. Without transactions only the OWN column renders.
   get transColumnVisible(): boolean {
      return this.editableLegs.some(l => l.isNew || l.crossedOut || !!l.qtySplit);
   }

   // Does the edit set derive at least one transaction delta? Structural mirror of the diff rules over the
   // dialog's edit primitives: a surviving NEW leg -> Open; a crossed EXISTING leg -> Close; a committed
   // qty split away from the original -> Increase/Decrease. Gates Review/Acquire (owner ruling 2026-06-10:
   // an unchanged scope has nothing to preview or book). Stricter than transColumnVisible — an OPEN but
   // empty qty split shows the trans column yet derives nothing.
   get hasTransaction(): boolean {
      const legs = this.editableLegs;
      for (let i = 0; i < legs.length; i++) {
         const l = legs[i];
         if (l.isNew && !l.crossedOut) { return true; }
         if (!l.isNew && l.crossedOut) { return true; }
         if (!l.isNew && l.qtySplit && this.isPositiveNumber(l.transQty) && l.transQty !== l.originalQty) {
            return true;
         }
      }
      return false;
   }

   // OWN cell: existing, not crossed-out legs only (a crossed leg prices in the TRANS cell as its close;
   // a new leg owns nothing) — HG getPriceboxVisibility(leg,'owned') analog.
   ownCellVisible(leg: MtEditableLeg): boolean {
      return !leg.isNew && !leg.crossedOut;
   }

   // TRANS cell: new legs, crossed-out legs, and qty-split legs — HG getPriceboxVisibility(leg,'trans').
   transCellVisible(leg: MtEditableLeg): boolean {
      return leg.isNew || leg.crossedOut || !!leg.qtySplit;
   }

   // Per-unit OWN price: quote signed by the closing action (long +, short -). Null until a quote arrives.
   ownPriceOf(leg: MtEditableLeg): number {
      const quote = leg.liveQuote;
      if (quote === null || quote === undefined) { return null; }
      return quote * leg.side;
   }

   // Per-unit TRANSACTION price (direct): crossed leg -> its close (own-signed, HG prices removed legs in
   // the trans column); new leg -> open (buy negative / sell positive); qty-split leg -> the net change's
   // direction (decrease a long = sell = positive, etc.); split-without-a-valid-qty -> blank.
   transPriceOf(leg: MtEditableLeg): number {
      const quote = leg.liveQuote;
      if (quote === null || quote === undefined) { return null; }
      if (leg.crossedOut) {
         return quote * leg.side;
      }
      if (leg.isNew) {
         return quote * leg.side * -1;
      }
      if (leg.qtySplit) {
         if (!this.isPositiveNumber(leg.transQty)) { return null; }
         const netQty = leg.side * (leg.transQty - leg.originalQty);
         if (netQty === 0) { return 0; }
         return quote * Math.sign(netQty) * -1;
      }
      return null;
   }

   // Sign-coloured price box (HG getPriceBoxStyleInputAttr clone): positive green / negative red / neutral.
   // Sub-epsilon float residue folds to neutral (HG parity — a displayed "$0.00" must never be coloured).
   priceBoxStyle(value: number): { style: string } {
      let style = 'text-align: center;';
      if (typeof value !== 'number' || !isFinite(value) || Math.abs(value) < Number.EPSILON) {
         style += 'color: inherit';
      } else if (value > 0) {
         style += 'color: green';
      } else {
         style += 'color: red';
      }
      return { style };
   }

   // Fold mixed-sign float residue to a clean 0 (HG getGroupTotalPrice tail-end normalization).
   private foldEpsilon(value: number): number {
      if (typeof value === 'number' && isFinite(value) && Math.abs(value) < Number.EPSILON) {
         return 0;
      }
      return value;
   }

   // ---- Totals (HG getGroupTotalPrice clone, normalized by the combo's Default Qty) ----
   //
   // OWN total = the owned structure's price: SUM(rawQuote x signedOriginalQty) over existing, not-crossed
   // OPTION legs, divided by the combo Default Qty. TRANS total = the transaction's cash flow: crossed legs
   // close at their own-signed price, qty-split legs contribute their net change (direct-signed), new legs
   // open direct-signed — same normalization. ASSET legs are EXCLUDED from both totals (owner decision —
   // a stock leg's notional dwarfs the option structure); their per-leg prices still show.
   // HG divides by findHCF then multiplies by hcf/defaultQty — the HCF cancels, so this computes the net
   // formula directly. Totals render only when the edited legs resolve to ONE combo (the Default Qty owner).

   get totalsVisible(): boolean {
      return this.singleScopeComboId() !== null;
   }

   get ownTotal(): number {
      const comboId = this.singleScopeComboId();
      if (comboId === null) { return null; }
      let sum = 0;
      const legs = this.editableLegs;
      for (let i = 0; i < legs.length; i++) {
         const l = legs[i];
         if (l.kind !== KIND_OPTION || l.isNew || l.crossedOut) { continue; }
         const quote = l.liveQuote;
         if (quote === null || quote === undefined) { return null; }   // any missing quote -> no total (HG)
         sum += quote * l.side * l.originalQty;
      }
      return this.foldEpsilon(sum / this.comboDefaultQty(comboId));
   }

   get transTotal(): number {
      const comboId = this.singleScopeComboId();
      if (comboId === null) { return null; }
      let sum = 0;
      const legs = this.editableLegs;
      for (let i = 0; i < legs.length; i++) {
         const l = legs[i];
         if (l.kind !== KIND_OPTION) { continue; }
         let contribution: number = null;
         const quote = l.liveQuote;
         if (l.crossedOut) {
            // Close the whole line: own-signed (long -> sell -> credit), at the ORIGINAL qty.
            if (quote === null || quote === undefined) { return null; }
            contribution = quote * l.side * l.originalQty;
         } else if (l.isNew) {
            // Open: direct-signed at the desired qty.
            if (quote === null || quote === undefined) { return null; }
            contribution = quote * l.side * l.targetQty * -1;
         } else if (l.qtySplit) {
            // An in-flight split (no valid desired qty yet) BLANKS the trans total — HG NaN-propagates to
            // a blank in this exact state; omitting the leg would show a misleading number while the
            // per-leg trans cell is blank.
            if (!this.isPositiveNumber(l.transQty)) { return null; }
            // Net change: direct-signed (decrease a long = sell = credit).
            if (quote === null || quote === undefined) { return null; }
            const netQty = l.side * (l.transQty - l.originalQty);
            contribution = quote * netQty * -1;
         } else {
            continue;   // unchanged leg -> no transaction contribution
         }
         sum += contribution;
      }
      return this.foldEpsilon(sum / this.comboDefaultQty(comboId));
   }

   // The combo's Default Qty off the loaded bucket tree (backend-persisted, CR-20260611-001). _buckets
   // reloads on every show(), so the value is current; an unknown id falls back to 1 (raw totals).
   private comboDefaultQty(comboId: string): number {
      return this._comboSettings.getDefaultQty(this._buckets.find(b => b.bucketId === comboId));
   }

   // The ONE combo the edited legs belong to, or null when unresolvable / spanning combos (multi-combo
   // portfolio scope -> totals hidden, v1). Group/Combo scope resolves from the scope bucket itself, so
   // totals work even when the scope holds no legs yet.
   private singleScopeComboId(): string | null {
      if (!this._config) { return null; }   // template getters can run before the first show()
      if (this._config.level === LEVEL_COMBO) { return this._config.scopeBucketId; }
      if (this._config.level === LEVEL_GROUP) {
         const group = this._buckets.find(b => b.bucketId === this._config.scopeBucketId);
         return group && group.parentBucketId ? group.parentBucketId : null;
      }
      // Portfolio scope: resolve each leg's group -> parent combo; totals only when exactly one combo.
      const comboIds: string[] = [];
      const legs = this.editableLegs;
      for (let i = 0; i < legs.length; i++) {
         const groupId = legs[i].groupBucketId;
         if (!groupId) { continue; }   // unrouted new leg — Save/Commit validation owns this case
         const group = this._buckets.find(b => b.bucketId === groupId);
         const comboId = group ? group.parentBucketId : null;
         if (comboId && comboIds.indexOf(comboId) < 0) { comboIds.push(comboId); }
      }
      return comboIds.length === 1 ? comboIds[0] : null;
   }

   // Build the per-leg live-quote limit map Copy-to-TOS sends as each leg's TOS limit (keyed by code).
   private livePxByCode(deltas: CollarsTransactionDelta[]): { [tradingInstrumentCode: string]: number } {
      const map: { [tradingInstrumentCode: string]: number } = {};
      (deltas || []).forEach(d => {
         const leg = this.editableLegs.find(l => !l.crossedOut && this.codeOf(l) === d.tradingInstrumentCode);
         const q = leg && !isVoid(leg.liveQuote) ? leg.liveQuote : undefined;
         if (!isVoid(q) && q > 0) {
            map[d.tradingInstrumentCode] = q;
         }
      });
      return map;
   }

   private survivingLegs(): MtEditableLeg[] {
      return this.editableLegs.filter(l => !l.crossedOut);
   }

   // Block any surviving leg with an empty/invalid instrument (option legs also need strike/expiration/type)
   // and any leg not routed to a group.
   private validateSurvivors(survivors: MtEditableLeg[]): boolean {
      for (let i = 0; i < survivors.length; i++) {
         const l = survivors[i];
         if (isVoid(l.groupBucketId)) {
            this._toastr.error('Every leg must be routed to a group');
            return false;
         }
         if (l.kind === KIND_OPTION) {
            if (isVoid(l.expiration)) { this._toastr.error('Each option leg needs an expiration'); return false; }
            if (isVoid(l.optionType)) { this._toastr.error('Each option leg needs a type (Call/Put)'); return false; }
            if (isVoid(l.strike) || l.strike <= 0) { this._toastr.error('Each option leg needs a strike'); return false; }
         }
         if (isVoid(l.qty) || l.qty <= 0) { this._toastr.error('Each leg needs a positive quantity'); return false; }
         const code = this.codeOf(l);
         if (isVoid(code) || !String(code).trim()) {
            this._toastr.error(l.isNew ? 'Pick an underlying for every new leg' : 'A leg has an invalid instrument');
            return false;
         }
      }

      // ONE underlying per transaction (owner rule 2026-06-10): a mixed package must never commit — the
      // picker lock + add-time guard prevent it by construction; this is the commit-time backstop.
      const underlyings = this.dialogUnderlyingKeys();
      if (underlyings.length > 1) {
         this._toastr.error(`One underlying per transaction — this package spans ${underlyings.join(', ')}`);
         return false;
      }

      // At most ONE asset leg in the end state (owner rule 2026-06-10). The Add Asset button already
      // prevents this preventively; this catches restore-after-add and any future entry path.
      const assetLegs = survivors.filter(l => l.kind === KIND_ASSET);
      if (assetLegs.length > 1) {
         this._toastr.error('Only one asset leg is allowed');
         return false;
      }

      // No two IDENTICAL legs: the diff/booking model holds ONE line per (group, instrument) — a duplicate
      // would silently collapse in the derived transaction and book wrong. Runs after the per-leg loop so
      // every code is composed and valid. (Same instrument in DIFFERENT groups is legitimate.)
      const seenLines: { [groupAndCode: string]: true } = {};
      for (let i = 0; i < survivors.length; i++) {
         const l = survivors[i];
         const lineKey = (l.groupBucketId || '') + '|' + this.codeOf(l);
         if (seenLines[lineKey]) {
            this._toastr.error(`Duplicate leg: ${this.legDisplayName(this.codeOf(l))} appears twice in the same group`);
            return false;
         }
         seenLines[lineKey] = true;
      }

      return true;
   }

   // Compose the real instrument code for every NEW survivor so the diff + booking see actual tickers.
   private composeNewLegCodes(): void {
      this.editableLegs.forEach(l => {
         if (l.isNew && !l.crossedOut) {
            const code = this.codeOf(l);
            if (!isVoid(code)) { l.tradingInstrumentCode = code; }
         }
      });
   }

   // The fully-composed instrument code for a leg:
   //   - EXISTING leg (not new)                         -> its stored position code.
   //   - NEW leg carrying a fixed (rolled / flipped) code -> that code (already valid; its expiration is a
   //     descriptor makeOptionTicker can recompose, but the baked code is authoritative).
   //   - GENUINELY new picker-driven leg                -> asset = underlying ticker; option = makeOptionTicker
   //     over the picker fields (expiration is an OptionExpirationDescriptor).
   private codeOf(leg: MtEditableLeg): string {
      if (!leg.isNew) { return leg.tradingInstrumentCode; }
      if (!isVoid(leg.tradingInstrumentCode) && String(leg.tradingInstrumentCode).trim()) {
         return leg.tradingInstrumentCode;
      }
      const inst = this.underlying;
      if (!inst) { return ''; }
      if (leg.kind === KIND_ASSET) { return inst.ticker; }
      const code = makeOptionTicker(leg.expiration, leg.optionType, leg.strike);
      return code || '';
   }

   // The persistable expiration of a leg: existing legs hold a date string; rolled/picker legs hold an
   // OptionExpirationDescriptor — take its optionExpirationDate (ISO yyyy-MM-dd).
   private expirationIsoOf(leg: MtEditableLeg): any {
      const e: any = leg.expiration;
      if (e && typeof e === 'object' && e.optionExpirationDate) {
         return e.optionExpirationDate;
      }
      return e;
   }

   private groupSurvivors(survivors: MtEditableLeg[]): { [groupId: string]: MtEditableLeg[] } {
      const map: { [groupId: string]: MtEditableLeg[] } = {};
      survivors.forEach(l => {
         const g = l.groupBucketId;
         if (!map[g]) { map[g] = []; }
         map[g].push(l);
      });
      return map;
   }

   // Flatten the per-group transaction into one ordered list (close-first within a group already holds).
   private flattenDeltasFrom(result: CollarsDiffResult): CollarsTransactionDelta[] {
      const out: CollarsTransactionDelta[] = [];
      (result.transaction || []).forEach(g => {
         (g.deltas || []).forEach(d => out.push(d));
      });
      return out;
   }

   private resolveWith(result: CollarsMakeTransactionResult): void {
      if (this._resolve) {
         this._resolve(result);
         this._resolve = null;
      }
   }
}
